import {
  useQueryClient,
  type InfiniteData,
  type Query,
  type QueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { matchPath, useLocation } from 'react-router';
import { API_BASE, fetchRealtimeCursor } from '../../api';
import { useSession } from '../../auth';
import { SEARCHES, invalidateConversationLists } from '../conversation';
import { applyEvent } from './apply-event';
import { lookupAssignee, resolveAssignee } from './assignee';
import type { Viewer } from './lists';
import { AttentionNotifier } from './notifier';
import { RealtimeSocket } from './socket';
import { createRealtimeStore, RealtimeStoreContext, type RealtimeStore } from './store';

/** Várias mudanças seguidas pedem UMA nova busca das listas. */
const REFRESH_LISTS_DELAY_MS = 400;

/** Consultas que são só do navegador (rascunhos, envios em andamento) ou a sessão: não vêm do servidor. */
const CLIENT_OWNED = new Set(['session', 'draft', 'outbox']);
const isServerData = (query: Query): boolean => !CLIENT_OWNED.has(String(query.queryKey[0]));

/** Endereço do WebSocket: o mesmo da API (`/api/ws`), com o esquema trocado. */
function realtimeUrl(): string {
  const url = new URL(`${API_BASE}/ws`, window.location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

/** Listas paginadas (conversas, histórico): a página 0 é sempre a mais recente. */
const PAGED = ['conversations', 'messages'] as const;

/**
 * Recomeça o que veio do servidor (o servidor não consegue retomar, ou o cache ficou num estado
 * desconhecido). O que ninguém está olhando é DESCARTADO em vez de marcado como velho; o que está
 * na tela é refeito no lugar, e as listas paginadas voltam à primeira página (as demais vêm sob
 * demanda): refazer todas as páginas guardadas seria uma fila de pedidos em série, que ainda
 * descarta tudo se um deles falhar.
 */
function resyncServerData(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (query) => isServerData(query) && !query.isActive() });
  for (const prefix of PAGED) {
    queryClient.setQueriesData<InfiniteData<unknown>>({ queryKey: [prefix] }, (cached) =>
      cached && cached.pages.length > 1
        ? { pages: cached.pages.slice(0, 1), pageParams: cached.pageParams.slice(0, 1) }
        : cached,
    );
  }
  void queryClient.invalidateQueries({ predicate: isServerData });
}

/**
 * Mantém UMA conexão em tempo real enquanto há alguém conectado e aplica os eventos aos caches. Fica
 * dentro do `AuthProvider` (precisa saber quem está conectado) e dentro do roteador (precisa saber
 * qual conversa está aberta). O estado da conexão vai para a interface pelo `RealtimeStore`.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [store] = useState(createRealtimeStore);
  const viewer: Viewer | null =
    session.status === 'authenticated' ? { id: session.user.id, role: session.user.role } : null;
  return (
    <RealtimeStoreContext value={store}>
      {/* Outra pessoa na mesma aba (troca de conta): o efeito depende do id e recomeça do zero
          (conexão, aviso sonoro, cursor). */}
      {viewer && <RealtimeConnection viewer={viewer} store={store} />}
      {children}
    </RealtimeStoreContext>
  );
}

function RealtimeConnection({ viewer, store }: { viewer: Viewer; store: RealtimeStore }) {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  // Os tratadores abaixo vivem tanto quanto a conexão; leem daqui o que muda a cada renderização.
  const openConversationId = useRef<string | null>(null);
  const role = useRef(viewer.role);
  useEffect(() => {
    openConversationId.current =
      matchPath('/c/:conversationId/*', pathname)?.params.conversationId ?? null;
    role.current = viewer.role;
  }, [pathname, viewer.role]);

  useEffect(() => {
    let disposed = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let attemptAt = Date.now();
    let hadReady = false;

    const refreshLists = (): void => {
      if (refreshTimer) return;
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        if (!disposed) void invalidateConversationLists(queryClient);
      }, REFRESH_LISTS_DELAY_MS);
    };

    const refreshSearches = (): void => {
      if (searchTimer) return;
      searchTimer = setTimeout(() => {
        searchTimer = null;
        if (!disposed) void queryClient.invalidateQueries({ queryKey: SEARCHES });
      }, REFRESH_LISTS_DELAY_MS);
    };

    const notifier = new AttentionNotifier({
      viewerId: viewer.id,
      openConversationId: () => openConversationId.current,
      assigneeOf: (id) => lookupAssignee(queryClient, id),
    });

    const socket = new RealtimeSocket(
      { url: realtimeUrl },
      {
        onAttempt: () => {
          attemptAt = Date.now();
        },
        onStatus: (status) => store.set(status),
        onReady: () => {
          if (hadReady) return;
          hadReady = true;
          // Primeira conexão que deu certo: o que foi carregado ANTES de ela começar (ou durante
          // tentativas que falharam) pode ter perdido eventos. O que chegou depois está coberto.
          void queryClient.invalidateQueries({
            predicate: (query) =>
              isServerData(query) &&
              query.state.fetchStatus === 'idle' &&
              query.state.dataUpdatedAt > 0 &&
              query.state.dataUpdatedAt < attemptAt,
          });
        },
        onEvent: (event) => {
          try {
            applyEvent(queryClient, event, {
              viewer: { id: viewer.id, role: role.current },
              refreshLists,
              refreshSearches,
            });
          } catch {
            // Cache num formato inesperado: pior que não aplicar é aplicar pela metade. Refaz tudo.
            resyncServerData(queryClient);
          }
          if (event.type !== 'message.created' || event.message.direction !== 'in') return;
          const { conversationId } = event.message;
          if (lookupAssignee(queryClient, conversationId) !== undefined) {
            notifier.handle(event);
            return;
          }
          // Conversa que esta tela nunca carregou: descobre o responsável antes de chamar a atenção.
          // Sem resposta (rede), na dúvida avisa.
          void resolveAssignee(queryClient, conversationId)
            .catch(() => undefined)
            .then(() => {
              if (!disposed) notifier.handle(event);
            });
        },
        onResync: () => resyncServerData(queryClient),
        onSessionEnded: async () => {
          // Sessão encerrada de verdade: este pedido dá 401, o app vai para o login e o provedor
          // desmonta (a conexão para). Sessão ainda válida: a conexão simplesmente recomeça.
          await fetchRealtimeCursor();
        },
      },
    );

    store.onResume(() => socket.resume());
    socket.start();
    return () => {
      disposed = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      if (searchTimer) clearTimeout(searchTimer);
      store.onResume(null);
      socket.stop();
      notifier.dispose();
      store.set('off');
    };
  }, [queryClient, store, viewer.id]);

  return null;
}
